import { formatWorkspaceRelativePath } from "./filePathDisplay";
import { resolvePathLinkTarget, splitPathAndPosition } from "./terminal-links";

const WINDOWS_DRIVE_PATH_PATTERN = /^[A-Za-z]:[\\/]/;
const WINDOWS_UNC_PATH_PATTERN = /^\\\\/;
const EXTERNAL_SCHEME_PATTERN = /^([A-Za-z][A-Za-z0-9+.-]*):(.*)$/;
const RELATIVE_PATH_PREFIX_PATTERN = /^(~\/|\.{1,2}\/)/;
const RELATIVE_FILE_PATH_PATTERN = /^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)+(?::\d+){0,2}$/;
const RELATIVE_FILE_NAME_PATTERN = /^[A-Za-z0-9._-]+\.[A-Za-z0-9_-]+(?::\d+){0,2}$/;
const POSITION_SUFFIX_PATTERN = /:\d+(?::\d+)?$/;
const POSITION_ONLY_PATTERN = /^\d+(?::\d+)?$/;
const POSIX_FILE_ROOT_PREFIXES = [
  "/Users/",
  "/home/",
  "/tmp/",
  "/var/",
  "/etc/",
  "/opt/",
  "/mnt/",
  "/Volumes/",
  "/private/",
  "/root/",
] as const;

/**
 * Local dev servers, written the way people actually write them.
 *
 * `localhost:5173` is not a link to any markdown parser -- it has no scheme --
 * but in a coding transcript it is always an address, and being told to copy it
 * into a browser by hand is the wrong end of the tool. The port is required:
 * "localhost" on its own is a word in a sentence far more often than it is a
 * place to go.
 */
const LOCALHOST_HOST_SOURCE = "(?:localhost|127\\.0\\.0\\.1|0\\.0\\.0\\.0)";
const BARE_LOCALHOST_URL_REGEX = new RegExp(
  `${LOCALHOST_HOST_SOURCE}:\\d{1,5}(?:[/?#][^\\s]*)?`,
  "gi",
);
const WHOLE_LOCALHOST_URL_REGEX = new RegExp(
  `^(https?://)?${LOCALHOST_HOST_SOURCE}(:\\d{1,5})?(?:[/?#][^\\s]*)?$`,
  "i",
);
/** Characters that make a `localhost:` run part of something longer. */
const LOCALHOST_BOUNDARY_BLOCKERS = /[A-Za-z0-9._\-/:@]/;
const URL_TRAILING_PUNCTUATION = ".,;:!?'\"";

export interface BareLocalhostUrlMatch {
  readonly start: number;
  readonly end: number;
  /** The run as written, so the transcript still reads the way it was sent. */
  readonly text: string;
  /** The same address made loadable. */
  readonly url: string;
}

function trimUrlTrailingPunctuation(value: string): string {
  let end = value.length;
  while (end > 0) {
    const char = value[end - 1]!;
    if (URL_TRAILING_PUNCTUATION.includes(char)) {
      end -= 1;
      continue;
    }
    if (char === ")") {
      const slice = value.slice(0, end);
      const opened = slice.split("(").length - 1;
      const closed = slice.split(")").length - 1;
      if (closed > opened) {
        end -= 1;
        continue;
      }
    }
    break;
  }
  return value.slice(0, end);
}

/**
 * Every bare `host:port` run in a stretch of plain text, in order.
 *
 * Scheme-less by design: text that already carries `http://` is turned into a
 * link by the markdown autolinker before this ever sees it.
 */
export function findBareLocalhostUrls(text: string): BareLocalhostUrlMatch[] {
  const matches: BareLocalhostUrlMatch[] = [];
  for (const match of text.matchAll(BARE_LOCALHOST_URL_REGEX)) {
    const start = match.index ?? 0;
    const previous = start === 0 ? "" : text[start - 1]!;
    if (previous !== "" && LOCALHOST_BOUNDARY_BLOCKERS.test(previous)) {
      continue;
    }
    const followingIndex = start + match[0].length;
    const following = text[followingIndex] ?? "";
    const afterFollowing = text[followingIndex + 1] ?? "";
    if (
      following !== "" &&
      LOCALHOST_BOUNDARY_BLOCKERS.test(following) &&
      // A full stop ending a sentence is punctuation, while `.example` means
      // the apparent port was only the start of a longer token.
      !(following === "." && !/[A-Za-z0-9_/-]/.test(afterFollowing))
    ) {
      continue;
    }
    const raw = trimUrlTrailingPunctuation(match[0]);
    if (!WHOLE_LOCALHOST_URL_REGEX.test(raw)) {
      continue;
    }
    matches.push({ start, end: start + raw.length, text: raw, url: `http://${raw}` });
  }
  return matches;
}

/**
 * The loadable address for a string that is entirely a localhost url, else null.
 *
 * Used for inline code spans, where the backticks already say "this is one
 * thing", so a scheme is optional and a bare host with a scheme but no port
 * (`http://localhost/health`) still counts.
 */
export function localhostUrlFromText(text: string): string | null {
  const trimmed = text.trim();
  const match = WHOLE_LOCALHOST_URL_REGEX.exec(trimmed);
  if (!match) {
    return null;
  }
  const scheme = match[1];
  const port = match[2];
  if (!scheme && !port) {
    return null;
  }
  return scheme ? trimmed : `http://${trimmed}`;
}

export interface MarkdownFileLinkMeta {
  filePath: string;
  targetPath: string;
  href: string;
  displayPath: string;
  basename: string;
  line?: number;
  column?: number;
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function unwrapMarkdownLinkDestination(value: string): string {
  return value.startsWith("<") && value.endsWith(">") ? value.slice(1, -1) : value;
}

export function normalizeMarkdownLinkDestination(value: string): string {
  return unwrapMarkdownLinkDestination(value.trim());
}

function stripSearchAndHash(value: string): { path: string; hash: string } {
  const hashIndex = value.indexOf("#");
  const pathWithSearch = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const rawHash = hashIndex >= 0 ? value.slice(hashIndex) : "";
  const queryIndex = pathWithSearch.indexOf("?");
  const path = queryIndex >= 0 ? pathWithSearch.slice(0, queryIndex) : pathWithSearch;
  return { path, hash: rawHash };
}

function normalizeWindowsDrivePath(path: string): string {
  return /^\/[A-Za-z]:[\\/]/.test(path) ? path.slice(1) : path;
}

function encodeFileUrlPath(path: string): string {
  return path
    .split("/")
    .map((segment, index) =>
      index === 0 && /^[A-Za-z]:$/.test(segment) ? segment : encodeURIComponent(segment),
    )
    .join("/");
}

export function toMarkdownFileUrlHref(
  filePath: string,
  position?: { readonly line?: number | undefined; readonly column?: number | undefined },
): string {
  const normalizedPath = filePath.replaceAll("\\", "/");
  const fileUrlPath = WINDOWS_DRIVE_PATH_PATTERN.test(normalizedPath)
    ? `/${encodeFileUrlPath(normalizedPath)}`
    : encodeFileUrlPath(normalizedPath);
  const line = position?.line;
  const column = position?.column;
  const hash = line ? `#L${line}${column ? `C${column}` : ""}` : "";
  return `file://${fileUrlPath}${hash}`;
}

function parseFileUrlHref(
  href: string,
  options?: { readonly decodePath?: boolean },
): { path: string; hash: string } | null {
  try {
    const parsed = new URL(href);
    if (parsed.protocol.toLowerCase() !== "file:") return null;

    const rawPath = parsed.pathname;
    if (rawPath.length === 0) return null;

    // Browser URL parser encodes "C:/foo" as "/C:/foo" for file URLs.
    const normalizedPath = normalizeWindowsDrivePath(rawPath);

    return {
      path: options?.decodePath === false ? normalizedPath : safeDecode(normalizedPath),
      hash: parsed.hash,
    };
  } catch {
    return null;
  }
}

export function rewriteMarkdownFileUriHref(href: string | undefined): string | null {
  if (!href) return null;
  const normalizedHref = normalizeMarkdownLinkDestination(href);
  const target = parseFileUrlHref(normalizedHref, { decodePath: false });
  if (!target) return null;
  return `${target.path}${target.hash}`;
}

function looksLikePosixFilesystemPath(path: string): boolean {
  if (!path.startsWith("/")) return false;
  if (POSIX_FILE_ROOT_PREFIXES.some((prefix) => path.startsWith(prefix))) return true;
  if (POSITION_SUFFIX_PATTERN.test(path)) return true;
  const basename = path.slice(path.lastIndexOf("/") + 1);
  return /\.[A-Za-z0-9_-]+$/.test(basename);
}

function appendLineColumnFromHash(path: string, hash: string): string {
  if (!hash || POSITION_SUFFIX_PATTERN.test(path)) return path;
  const match = hash.match(/^#L(\d+)(?:C(\d+))?$/i);
  if (!match?.[1]) return path;
  const line = match[1];
  const column = match[2];
  return `${path}:${line}${column ? `:${column}` : ""}`;
}

function isLikelyPathCandidate(path: string): boolean {
  if (WINDOWS_DRIVE_PATH_PATTERN.test(path) || WINDOWS_UNC_PATH_PATTERN.test(path)) return true;
  if (RELATIVE_PATH_PREFIX_PATTERN.test(path)) return true;
  if (path.startsWith("/")) return looksLikePosixFilesystemPath(path);
  return RELATIVE_FILE_PATH_PATTERN.test(path) || RELATIVE_FILE_NAME_PATTERN.test(path);
}

function isRelativePath(path: string): boolean {
  return (
    RELATIVE_PATH_PREFIX_PATTERN.test(path) ||
    (!path.startsWith("/") &&
      !WINDOWS_DRIVE_PATH_PATTERN.test(path) &&
      !WINDOWS_UNC_PATH_PATTERN.test(path))
  );
}

function hasExternalScheme(path: string): boolean {
  const match = path.match(EXTERNAL_SCHEME_PATTERN);
  if (!match) return false;
  const rest = match[2] ?? "";
  if (rest.startsWith("//")) return true;
  return !POSITION_ONLY_PATTERN.test(rest);
}

export function resolveMarkdownFileLinkTarget(
  href: string | undefined,
  cwd?: string,
): string | null {
  if (!href) return null;
  const rawHref = normalizeMarkdownLinkDestination(href);
  if (rawHref.length === 0 || rawHref.startsWith("#")) return null;
  if (localhostUrlFromText(rawHref) !== null) return null;

  const fileUrlTarget = rawHref.toLowerCase().startsWith("file:")
    ? parseFileUrlHref(rawHref)
    : null;
  const source = fileUrlTarget ?? stripSearchAndHash(rawHref);
  const decodedPath = normalizeWindowsDrivePath(
    fileUrlTarget ? source.path.trim() : safeDecode(source.path.trim()),
  );
  const decodedHash = safeDecode(source.hash.trim());

  if (decodedPath.length === 0) return null;
  if (
    !WINDOWS_DRIVE_PATH_PATTERN.test(decodedPath) &&
    !WINDOWS_UNC_PATH_PATTERN.test(decodedPath) &&
    hasExternalScheme(decodedPath)
  ) {
    return null;
  }

  if (!isLikelyPathCandidate(decodedPath)) return null;

  const pathWithPosition = appendLineColumnFromHash(decodedPath, decodedHash);
  if (!isRelativePath(pathWithPosition)) {
    return pathWithPosition;
  }

  if (!cwd) return null;
  return resolvePathLinkTarget(pathWithPosition, cwd);
}

function basenameOfPath(path: string): string {
  const pathWithoutTrailingSeparators = path.replace(/[\\/]+$/u, "");
  if (pathWithoutTrailingSeparators.length === 0) {
    return path;
  }

  const separatorIndex = Math.max(
    pathWithoutTrailingSeparators.lastIndexOf("/"),
    pathWithoutTrailingSeparators.lastIndexOf("\\"),
  );
  return separatorIndex >= 0
    ? pathWithoutTrailingSeparators.slice(separatorIndex + 1)
    : pathWithoutTrailingSeparators;
}

export function resolveMarkdownFileLinkMeta(
  href: string | undefined,
  cwd?: string,
): MarkdownFileLinkMeta | null {
  const targetPath = resolveMarkdownFileLinkTarget(href, cwd);
  if (!targetPath) return null;

  const { path, line, column } = splitPathAndPosition(targetPath);
  const parsedLine = line ? Number.parseInt(line, 10) : Number.NaN;
  const parsedColumn = column ? Number.parseInt(column, 10) : Number.NaN;
  const lineNumber = Number.isFinite(parsedLine) ? parsedLine : undefined;
  const columnNumber = Number.isFinite(parsedColumn) ? parsedColumn : undefined;

  return {
    filePath: path,
    targetPath,
    href: toMarkdownFileUrlHref(path, { line: lineNumber, column: columnNumber }),
    displayPath: formatWorkspaceRelativePath(targetPath, cwd),
    basename: basenameOfPath(path),
    ...(lineNumber !== undefined ? { line: lineNumber } : {}),
    ...(columnNumber !== undefined ? { column: columnNumber } : {}),
  };
}
